import { createClient } from 'graphql-ws';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { RetryLink } from '@apollo/client/link/retry';
import { ApolloClient, ApolloLink, FetchResult, Observable, from, split } from '@apollo/client';
import { getMainDefinition, isArray } from '@apollo/client/utilities';

import { createUploadLink } from 'apollo-upload-client';

import { toast } from 'react-toastify';

import { getApiUrl, getGraphQLUrl, getGraphQLWebSocketUrl } from './graphql-utils';
import { clearTokens, getAccessToken } from './storage';
import { cache } from './cache';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { logError } from 'helpers/logger';
import { createTokenRefreshLink } from './token-refresh-link';

// Types pour une meilleure gestion des erreurs
type ErrorExtensions = {
    code?: string;
    level?: string;
    status?: string;
    stacktrace?: string[] | string;
    [key: string]: unknown;
};

type ErrorLevel = 'error' | 'warning' | 'info';

// Configuration des erreurs
type ErrorConfig = {
    level: ErrorLevel;
    autoClose: number | false;
    showUser: boolean;
    redirectToLogin?: boolean;
};

const ERROR_CONFIG: Record<string, ErrorConfig> = {
    BAD_USER_INPUT: {
        level: 'warning',
        autoClose: false,
        showUser: true,
    },
    UNAUTHENTICATED: {
        level: 'error',
        autoClose: false,
        showUser: true,
        redirectToLogin: true,
    },
    AHEEX: {
        level: 'info',
        autoClose: 0,
        showUser: false,
    },
    // Genuine "not authenticated" on a query/mutation → the session is dead, log out.
    // (On a subscription, AHERA is a transient stale-WS-token and is handled by the
    // token-refresh link instead — see errorLink below.)
    AHERA: {
        level: 'error',
        autoClose: false,
        showUser: false,
        redirectToLogin: true,
    },
    FORBIDDEN: {
        level: 'error',
        autoClose: 8000,
        showUser: true,
    },
    INTERNAL_SERVER_ERROR: {
        level: 'error',
        autoClose: false,
        showUser: true,
    },
    NETWORK_ERROR: {
        level: 'error',
        autoClose: 10000,
        showUser: true,
    },
};

// Fonction utilitaire pour extraire le message d'erreur utilisateur
function getUserFriendlyMessage(error: any, extensions?: ErrorExtensions): string {
    if (extensions?.stacktrace && isArray(extensions.stacktrace) && extensions.stacktrace.length > 0) {
        return extensions.stacktrace[0];
    }

    if (extensions?.stacktrace && typeof extensions.stacktrace === 'string') {
        return extensions.stacktrace;
    }

    return error.message || "Une erreur inattendue s'est produite";
}

// Fonction pour afficher les erreurs avec la bonne configuration
function displayError(error: any, errorCode: string): void {
    const config = ERROR_CONFIG[errorCode as keyof typeof ERROR_CONFIG] || ERROR_CONFIG.INTERNAL_SERVER_ERROR;

    if (config.showUser) {
        const message = getUserFriendlyMessage(error, error.extensions);

        switch (config.level) {
            case 'warning':
                toast.warning(message, { autoClose: config.autoClose });
                break;
            case 'info':
                toast.info(message, { autoClose: config.autoClose });
                break;
            default:
                toast.error(message, { autoClose: config.autoClose });
        }
    }

    // Gestion spéciale pour les erreurs d'authentification
    if (config.redirectToLogin) {
        clearTokens();
        window.location.href = '/login';
    }
}

const graphQLWebSockerUrl = getGraphQLWebSocketUrl();

export const subscriptionClient = createClient({
    url: graphQLWebSockerUrl,
    shouldRetry: () => true, // client should not throw if server restarts
    connectionParams: () => ({
        token: getAccessToken(),
    }),
});

const wsLink = new GraphQLWsLink(subscriptionClient);

const graphQLUrl = getGraphQLUrl();

const uploadLink = createUploadLink({ uri: graphQLUrl });

const authLink = setContext((_, { headers }) => {
    const authorization = getAccessToken() || '';
    return {
        headers: {
            ...headers,
            'apollo-require-preflight': true, // https://www.apollographql.com/docs/apollo-server/security/cors/
            authorization,
        },
    };
});

// Transient network failures ("Failed to fetch" — a brief connectivity blip, VPN hiccup or a
// server restart) surface as a network error and otherwise bubble straight to the errorLink as a
// toast + Sentry report (see TOTADMIN-62E). Retry queries a few times so a momentary blip stays
// invisible to the user. Scoped to queries only: mutations may not be idempotent (retrying risks
// double-submits) and subscriptions reconnect through their own graphql-ws retry.
const retryLink = new RetryLink({
    attempts: {
        max: 3, // initial request + 2 retries
        retryIf: (error, operation) => {
            const definition = getMainDefinition(operation.query);
            const isQuery = definition.kind === 'OperationDefinition' && definition.operation === 'query';
            return Boolean(error) && isQuery;
        },
    },
    delay: {
        initial: 300,
        max: 3000,
        jitter: true,
    },
});

// we don't need httpLink because uploadLink already integrates it.
// Moreover, we can only use on terminating Link, and uploadLink is considered as a terminating Link (because of its httpLink)
export function createApolloClient() {
    const errorLink = new ApolloLink((operation, forward) => {
        return new Observable<FetchResult>((observer) => {
            const operationName = operation.operationName;
            const definition = getMainDefinition(operation.query);
            const isSubscription = definition.kind === 'OperationDefinition' && definition.operation === 'subscription';

            forward(operation).subscribe({
                next: (result) => {
                    if (result.errors) {
                        result.errors.forEach((error) => {
                            const errorCode = (error.extensions as ErrorExtensions)?.code || '';
                            const isUnauthenticated =
                                errorCode === 'AHERA' || error.message === 'Authentification requise';

                            // A stale WS token surfaces as AHERA on subscriptions roughly every
                            // 15min (the server can't emit AHEEX over the socket). The token-refresh
                            // link transparently refreshes the token and reconnects the socket, so
                            // this is expected churn — don't toast the user or report it to Sentry.
                            if (isSubscription && isUnauthenticated) {
                                return;
                            }

                            // AHEEX is the normal token-expiry signal, handled by the token-refresh
                            // link (refresh + retry). Surfacing it would spam toasts and Sentry.
                            if (errorCode === 'AHEEX') {
                                return;
                            }

                            displayError(error, errorCode);

                            logError({
                                message: `[GraphQLError] ${error.message}`,
                                tags: { operationName },
                                contexts: {
                                    error,
                                    operationVariables: operation.variables,
                                },
                            });
                        });
                    }
                    observer.next(result);
                },
                error: (networkError) => {
                    displayError(networkError, 'NETWORK_ERROR');

                    logError({
                        message: `[NetworkError] ${networkError.message}`,
                        tags: { operationName },
                        contexts: {
                            error: networkError,
                            operationVariables: operation.variables,
                        },
                    });

                    observer.error(networkError);
                },
                complete: () => observer.complete(),
            });
        });
    });

    const removeTypenameLink = removeTypenameFromVariables();
    const apiUrl = getApiUrl();
    const tokenRefreshLink = createTokenRefreshLink(apiUrl, () => subscriptionClient.terminate());
    return new ApolloClient({
        cache,
        link: from([
            removeTypenameLink,
            tokenRefreshLink,
            errorLink,
            authLink,
            split(
                ({ query }) => {
                    const definition = getMainDefinition(query);
                    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
                },
                wsLink,
                // Retry transient network errors on the HTTP path only (subscriptions reconnect via
                // graphql-ws). Sits downstream of errorLink so a request that recovers on retry never
                // reaches the toast/Sentry handler — only a definitive failure does.
                from([
                    retryLink,
                    // TS issue : apollo-upload-client incorrect typing (https://github.com/DefinitelyTyped/DefinitelyTyped/issues/47369)
                    uploadLink as unknown as ApolloLink,
                ]),
            ),
        ]),
    });
}
